import React, { useState, useEffect } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Link } from 'react-router-dom';
import Booknow from '../common/Button';
import 'swiper/css';
import 'swiper/css/pagination';
import { fetchInternationalPackages } from '../utils/api';
import { slugify } from '../utils/slug';

const InternationalPackages = () => {
  const [favorites, setFavorites] = useState([]);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define the exact list of titles you want to display, including the trailing space if your API adds it
  const desiredTitles = ['Dubai ', 'Thailand ', 'Bali ', 'Singapore '];

  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoading(true);
        const data = await fetchInternationalPackages();

        // Filter for 'category' type AND match the desired titles
        let formattedPackages = data
          .filter(
            (item) =>
              item.type === 'category' &&
              desiredTitles.includes(`${item.name} `)
          )
          .map((item) => ({
            _id: item._id,
            title: `${item.name} `,
            image: `${process.env.REACT_APP_API_BASE_URL}/${item.image}`,
          }));

        // Sort to ensure fixed display order: Dubai, Thailand, Bali, Singapore
        formattedPackages = formattedPackages.sort((a, b) => {
          return desiredTitles.indexOf(a.title) - desiredTitles.indexOf(b.title);
        });

        setPackages(formattedPackages);

      } catch (err) {
        setError('Failed to load packages. Please try again later.');
        console.error('Error fetching packages:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPackages();
  }, []);

  const toggleFavorite = (id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]));
  };

  const handleMouseEnter = () => {
    if (swiperInstance) swiperInstance.autoplay.stop();
  };

  const handleMouseLeave = () => {
    if (swiperInstance) swiperInstance.autoplay.start();
  };

  if (error) {
    return <div className='py-8 text-center text-red-500'>{error}</div>;
  }

  const SkeletonCard = () => (
    <div className='relative w-full animate-pulse rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'>
      <div className='relative'>
        <div className='h-[220px] w-full rounded-lg bg-gray-200'></div>
        <div className='absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200'></div>
      </div>
      <div className='mt-2 flex flex-col items-center p-3 text-center'>
        <div className='h-6 w-3/4 rounded bg-gray-200'></div>
      </div>
      <div className='mt-2 h-10 w-full rounded bg-gray-200'></div>
    </div>
  );

  return (
    <div className='bg-[#EDEFFF] pb-10 lg:pb-0'>
      <div className='container mx-auto px-4 lg:px-16'>
        <div className='flex max-w-full items-center justify-between py-8'>
          <p className='text-2xl font-medium leading-tight tracking-normal text-[#495BFF] md:text-[40px]'>
            International Packages
          </p>
          <Link
            to='/international'
            className='group flex items-center gap-1 text-[16px] font-semibold text-[#495BFF] md:text-[20px]'
          >
            See all
            <FiArrowUpRight className='transform text-[24px] text-[#55E6A5] transition-transform duration-500 group-hover:rotate-45' />
          </Link>
        </div>

        {/* Desktop Swiper */}
        <div className='hidden md:block'>
          <Swiper
            modules={[Autoplay, Pagination]}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            pagination={{
              clickable: true,
              renderBullet: (index, className) =>
                `<span class="${className} custom-bullet"></span>`,
            }}
            spaceBetween={20}
            slidesPerView={4}
            slidesPerGroup={4}
            loop={true}
            breakpoints={{
              0: { slidesPerView: 2, slidesPerGroup: 2 },
              640: { slidesPerView: 2, slidesPerGroup: 2 },
              1024: { slidesPerView: 4, slidesPerGroup: 4 },
            }}
            className='pb-[70px]'
            onSwiper={setSwiperInstance}
          >
            {loading
              ? Array(4)
                .fill()
                .map((_, index) => (
                  <SwiperSlide key={index}>
                    <SkeletonCard />
                  </SwiperSlide>
                ))
              : packages.map((pkg) => ( // REMOVED .slice(0, 8)
                <SwiperSlide
                  key={pkg._id}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className='relative w-full rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'>
                    <div className='relative'>
                      <Link
                        to={`/international/${slugify(pkg.title)}`}
                        className='block overflow-hidden rounded-lg'
                      >
                        <img
                          src={pkg.image}
                          alt={pkg.title}
                          className='h-[220px] w-full object-cover transition-transform duration-500 ease-in-out hover:scale-110'
                        />
                      </Link>

                      <button
                        onClick={() => toggleFavorite(pkg._id)}
                        aria-label={`Favorite ${pkg.title}`}
                        className={`absolute -bottom-3 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-[#55E6A5] shadow-md transition ${favorites.includes(pkg._id)
                            ? 'bg-[#495BFF] text-white'
                            : 'bg-white text-[#495BFF]'
                          }`}
                      >
                        <i
                          className={`${favorites.includes(pkg._id) ? 'fas fa-heart' : 'far fa-heart'
                            } text-md`}
                        ></i>
                      </button>
                    </div>
                    <Link to={`/international/${slugify(pkg.title)}`}>
                      <div className='mt-2 flex flex-col items-center p-3 text-center'>
                        <h3 className='flex items-start gap-1 font-semibold text-[#495BFF] hover:text-[#55E6A5] transition-colors duration-300 ease-in-out'>
                          <i className='fas fa-map-marker-alt mt-1 text-[#FF0000]'></i>
                          {pkg.title}
                        </h3>
                      </div>
                    </Link>
                    <Booknow category={pkg} />
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
        </div>

        {/* Mobile Horizontal Scroll */}
        <div className='custom-scrollbar flex gap-4 overflow-x-auto md:hidden'>
          {loading
            ? Array(4)
              .fill()
              .map((_, index) => (
                <div
                  key={index}
                  className='w-[250px] flex-none'
                >
                  <SkeletonCard />
                </div>
              ))
            : packages.map((pkg) => ( // REMOVED .slice(0, 8)
              <div
                key={pkg._id}
                className='relative w-[250px] flex-none rounded-lg border border-[#55E6A5] bg-white p-4 shadow-md'
              >
                <div className='relative'>
                  <Link to={`/international/${slugify(pkg.title)}`}>
                    <img
                      src={pkg.image}
                      alt={pkg.title}
                      className='h-[160px] w-full rounded-lg object-cover'
                    />
                  </Link>
                  <button
                    onClick={() => toggleFavorite(pkg._id)}
                    aria-label={`Favorite ${pkg.title}`}
                    className={`absolute -bottom-3 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-[#55E6A5] shadow-md transition ${favorites.includes(pkg._id)
                        ? 'bg-[#495BFF] text-white'
                        : 'bg-white text-[#495BFF]'
                      }`}
                  >
                    <i
                      className={`${favorites.includes(pkg._id) ? 'fas fa-heart' : 'far fa-heart'
                        } text-md`}
                    ></i>
                  </button>
                </div>
                <Link to={`/international/${slugify(pkg.title)}`}>
                  <div className='mt-2 flex flex-col items-center p-3 text-center'>
                    <h3 className='flex items-start gap-1 font-semibold text-[#495BFF] hover:text-[#55E6A5] transition-colors duration-300 ease-in-out'>
                      <i className='fas fa-map-marker-alt mt-1 text-[#FF0000]'></i>
                      {pkg.title}
                    </h3>
                  </div>
                </Link>
                <Booknow category={pkg} />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default InternationalPackages;